import { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api, type Restaurant, type Store } from './lib/api';

/** The store scope every page works inside: ids from the URL, names and
 * timezone fetched once per mount. */
export type Scope = {
  rid: string;
  sid: string;
  restaurant: Restaurant | null;
  store: Store | null;
};

const ScopeCtx = createContext<Scope | null>(null);

export function useScope(): Scope {
  const s = useContext(ScopeCtx);
  if (!s) throw new Error('useScope outside provider');
  return s;
}

/** Base API path for the current store. */
export function useBase(): string {
  const { rid, sid } = useScope();
  return `/restaurants/${rid}/stores/${sid}`;
}

export function ScopeProvider(props: { children: React.ReactNode }) {
  const { rid = '', sid = '' } = useParams();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [store, setStore] = useState<Store | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const [r, ss] = await Promise.all([
        api.get<Restaurant>(`/restaurants/${rid}`),
        api.get<Store[]>(`/restaurants/${rid}/stores`),
      ]);
      if (!live) return;
      setRestaurant(r);
      setStore(ss.find((s) => s.storeId === sid) ?? null);
    })();
    return () => {
      live = false;
    };
  }, [rid, sid]);

  return <ScopeCtx.Provider value={{ rid, sid, restaurant, store }}>{props.children}</ScopeCtx.Provider>;
}
